"use client";

import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useMemo, useState } from "react";

import { TableCell, TableHead } from "@krea/shared/design-system/table";

import type {
  RepaymentCapacityBalanceEntry,
  RepaymentCapacityProfitLossEntry,
  RepaymentCapacityRatioEntry,
} from "./types";

type KeyRatiosTableProps = {
  profitLossEntries: RepaymentCapacityProfitLossEntry[];
  ratioEntries: RepaymentCapacityRatioEntry[];
  balanceEntries: RepaymentCapacityBalanceEntry[];
  currency: string;
};

// ── Formatters ───────────────────────────────────────────────────────────────

const fmtCurrency = (v: number | null): string => {
  if (v == null) return "—";
  return new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(v);
};

function fmtPct(v: number | null): string {
  if (v == null) return "—";
  return `${(v * 100).toFixed(2)} %`;
}

function fmtRatio(v: number | null): string {
  if (v == null) return "—";
  return v.toFixed(2);
}

// ── Row / section types ───────────────────────────────────────────────────────

type RowDef<T> = {
  label: string;
  field?: keyof T;
  compute?: (e: T) => number | null;
  bold?: boolean;
  format: (v: number | null) => string;
};

type SectionDef<T> = {
  title: string;
  rows: RowDef<T>[];
};

// ── Row definitions ──────────────────────────────────────────────────────────

function incomeStatement(): SectionDef<RepaymentCapacityProfitLossEntry>[] {
  const c = fmtCurrency;
  return [
    {
      title: "",
      rows: [
        { label: "Revenues", field: "revenue", bold: true, format: c },
        { label: "Personnel costs", field: "personnelCosts", format: c },
        { label: "Other operating costs", field: "operatingCosts", format: c },
        { label: "EBITDA", field: "ebitda", bold: true, format: c },
      ],
    },
    {
      title: "",
      rows: [
        {
          label: "Depreciation & amortisation",
          field: "depreciationAndWritedowns",
          format: c,
        },
        { label: "EBIT", field: "ebit", bold: true, format: c },
      ],
    },
    {
      title: "",
      rows: [
        { label: "Financial incomes", field: "financialIncomes", format: c },
        { label: "Financial cost", field: "financialExpenses", format: c },
        {
          label: "Extraordinary income & expenses",
          field: "extraordinaryIncomeAndExpenses",
          format: c,
        },
        {
          label: "Profit before tax",
          field: "currentProfit",
          bold: true,
          format: c,
        },
      ],
    },
    {
      title: "",
      rows: [
        { label: "Appropriations", field: "appropriations", format: c },
        { label: "Tax", field: "tax", format: c },
        { label: "Net profit", field: "result", bold: true, format: c },
      ],
    },
  ];
}

function balanceSheet(): SectionDef<RepaymentCapacityBalanceEntry>[] {
  const c = fmtCurrency;
  return [
    {
      title: "Assets",
      rows: [
        {
          label: "Cash & cash equivalents",
          field: "cashAndCashEquivalent",
          format: c,
        },
        {
          label: "Trade receivables",
          field: "tradeReceivables",
          format: c,
        },
        {
          label: "Other receivables",
          field: "otherReceivables",
          format: c,
        },
        {
          label: "Other current assets",
          field: "otherCurrentReceivables",
          format: c,
        },
        {
          label: "Inventories",
          field: "warehouseAndWorkInProgress",
          format: c,
        },
        {
          label: "Total current assets",
          field: "totalLiquidAssets",
          bold: true,
          format: c,
        },
        {
          label: "Land & building",
          field: "buildingsAndFreeholdLand",
          format: c,
        },
        {
          label: "Equipment & machines",
          field: "equipmentsAndTools",
          format: c,
        },
        {
          label: "Financial fixed assets",
          field: "financialFixAssets",
          format: c,
        },
        {
          label: "Other tangible assets",
          field: "otherTangibleAssets",
          format: c,
        },
        { label: "Intangible assets", field: "intangibleAssets", format: c },
        {
          label: "Total fixed assets",
          field: "totalFixAssets",
          bold: true,
          format: c,
        },
        { label: "Total assets", field: "totalAssets", bold: true, format: c },
      ],
    },
    {
      title: "Equity & liabilities",
      rows: [
        { label: "Restricted equity", field: "restrictedEquity", format: c },
        {
          label: "Unrestricted equity",
          field: "unrestrictedEquity",
          format: c,
        },
        { label: "Total equity", field: "equity", bold: true, format: c },
        { label: "Accounts payables", field: "accountPayable", format: c },
        {
          label: "Short-term liabilities to credit institutions",
          field: "shortTermLiabilitiesToCreditInstitutions",
          format: c,
        },
        {
          label: "Other short-term liabilities",
          field: "otherCurrentLiabilities",
          format: c,
        },
        {
          label: "Total short-term liabilities",
          field: "currentLiabilities",
          bold: true,
          format: c,
        },
        {
          label: "Long-term liabilities to credit institutions",
          field: "longTermLiabilitiesToCreditInstitutions",
          format: c,
        },
        {
          label: "Other long-term liabilities",
          field: "otherLongtermLiabilities",
          format: c,
        },
        {
          label: "Total long-term liabilities",
          field: "totalLongtermLiabilities",
          bold: true,
          format: c,
        },
        {
          label: "Total equity & liabilities",
          field: "sumOfLiabilitiesAndEquity",
          bold: true,
          format: c,
        },
      ],
    },
  ];
}

const financialRatios: SectionDef<RepaymentCapacityRatioEntry>[] = [
  {
    title: "",
    rows: [
      { label: "EBIT margin", field: "operatingMargin", format: fmtPct },
      { label: "Quick ratio", field: "quickRatio", format: fmtRatio },
      { label: "Solidity", field: "solvency", format: fmtPct },
      {
        label: "Interest coverage ratio",
        field: "interestCoverageRatio",
        format: fmtRatio,
      },
      {
        label: "Net profit margin",
        field: "profitRatio",
        format: fmtPct,
      },
    ],
  },
];

// ── Cell value resolver ───────────────────────────────────────────────────────

function resolveCellValue<
  T extends { period: string; financialYearEnd: string },
>(entry: T | undefined, row: RowDef<T>): number | null {
  if (entry == null) return null;
  if (row.compute) return row.compute(entry);
  return entry[row.field as keyof T] as number | null;
}

// ── Unified period type ───────────────────────────────────────────────────────

type DisplayYear = { financialYearEnd: string; period: string; year: number };

// ── Sub-table ────────────────────────────────────────────────────────────────

const VISIBLE_YEARS = 4;

function SubTable<T extends { period: string; financialYearEnd: string }>({
  entries,
  sections,

  displayYears,
}: {
  entries: T[];
  sections: SectionDef<T>[];
  displayYears: DisplayYear[];
}) {
  const byYearEnd = new Map(entries.map((e) => [e.financialYearEnd, e]));

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-100">
            <TableHead className="px-4 py-3 w-48 text-table-header" />
            {displayYears.map((dy) => (
              <TableHead
                key={dy.financialYearEnd}
                className={`text-right px-3 py-3 tabular-nums text-table-header `}
              >
                {dy.period}
              </TableHead>
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.map((section, si) => (
            <React.Fragment key={si}>
              {section.title && (
                <tr key={`title-${si}`}>
                  <TableCell
                    colSpan={displayYears.length + 1}
                    className="px-4 pt-5 pb-1 text-subsection"
                  >
                    {section.title}
                  </TableCell>
                </tr>
              )}
              {si > 0 && !section.title && (
                <tr key={`spacer-${si}`}>
                  <TableCell
                    colSpan={displayYears.length + 1}
                    className="py-2"
                  />
                </tr>
              )}
              {section.rows.map((row) => (
                <tr
                  key={row.label}
                  className="border-t border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <TableCell
                    className={
                      row.bold
                        ? "px-4 py-2 text-sm font-semibold"
                        : "px-4 py-2 text-table-cell"
                    }
                  >
                    {row.label}
                  </TableCell>
                  {displayYears.map((dy) => {
                    const entry = byYearEnd.get(dy.financialYearEnd);
                    const raw = resolveCellValue(entry, row);
                    return (
                      <TableCell
                        key={dy.financialYearEnd}
                        className={`text-right px-3 py-2 tabular-nums ${row.bold ? "font-semibold" : ""}`}
                      >
                        {row.format(raw)}
                      </TableCell>
                    );
                  })}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Card wrapper ──────────────────────────────────────────────────────────────

function TableCard({
  header,
  children,
}: {
  header: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
        <h4 className="text-subsection">{header}</h4>
      </div>
      {children}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function KeyRatiosTable({
  profitLossEntries,
  ratioEntries,
  balanceEntries,
  currency,
}: KeyRatiosTableProps) {
  const [windowStart, setWindowStart] = useState(0);

  const unified = useMemo<DisplayYear[]>(() => {
    const map = new Map<string, DisplayYear>();
    for (const e of [
      ...profitLossEntries,
      ...balanceEntries,
      ...ratioEntries,
    ]) {
      if (!map.has(e.financialYearEnd)) {
        map.set(e.financialYearEnd, {
          financialYearEnd: e.financialYearEnd,
          period: e.period,
          year: dayjs(e.financialYearEnd).year(),
        });
      }
    }
    return [...map.values()].sort(
      (a, b) =>
        dayjs(b.financialYearEnd).valueOf() -
        dayjs(a.financialYearEnd).valueOf(),
    );
  }, [profitLossEntries, balanceEntries, ratioEntries]);

  if (
    profitLossEntries.length === 0 &&
    ratioEntries.length === 0 &&
    balanceEntries.length === 0
  ) {
    return <div className="text-helper">No financial data available</div>;
  }

  const newestYear = unified[0]?.year ?? 0;
  const displayYears = unified.slice(windowStart, windowStart + VISIBLE_YEARS);
  const canShowOlder = windowStart + VISIBLE_YEARS < unified.length;
  const canShowNewer = windowStart > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-helper">Amounts in {currency}</p>
        {unified.length > VISIBLE_YEARS && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setWindowStart((w) => w - 1)}
              disabled={!canShowNewer}
              className="rounded p-1 text-primary-400 hover:bg-primary-150 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Show newer years"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setWindowStart((w) => w + 1)}
              disabled={!canShowOlder}
              className="rounded p-1 text-primary-400 hover:bg-primary-150 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Show older years"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {profitLossEntries.length > 0 && (
        <TableCard header="Profit & loss">
          <SubTable
            entries={profitLossEntries}
            sections={incomeStatement()}
            displayYears={displayYears}
          />
        </TableCard>
      )}

      {balanceEntries.length > 0 && (
        <TableCard header="Balance sheet">
          <SubTable
            entries={balanceEntries}
            sections={balanceSheet()}
            displayYears={displayYears}
          />
        </TableCard>
      )}

      {ratioEntries.length > 0 && (
        <TableCard header="Financial ratio">
          <SubTable
            entries={ratioEntries}
            sections={financialRatios}
            displayYears={displayYears}
          />
        </TableCard>
      )}
    </div>
  );
}
